import { Session } from 'next-auth'
import { MySubmissionSummary } from '../MySubmissionSummary'
import { RoundDetails, Team } from '@/types'
import { isPast } from 'date-fns'
import { CtaPanel } from '../CtaPanel'
import { ChallengeTheme } from '@/types/Event'
import { TeamAction, TeamPanel } from '../Team'
import { deleteTeam, leaveTeam } from '../../team/actions'
import { toast } from '@/lib/utils/toast'
import { useRouter } from 'next/navigation'

export interface TeamAndSubmissionProps {
  eventSlug: string
  session: Session
  team?: Team
  rounds: RoundDetails[]
  themes: ChallengeTheme[]
}

export const TeamAndSubmission = ({
  eventSlug,
  session,
  team,
  rounds,
  themes,
}: TeamAndSubmissionProps) => {
  const isTeamLeader =
    !!team &&
    team.team_members.find((m) => m.role === 'leader')?.username ===
      session.user.username

  const isMember =
    !!team &&
    team.team_members.find((m) => m.username === session.user.username)
      ?.role === 'member'

  const submissionStarted =
    rounds.length > 0 && isPast(rounds[0].submission_start)

  let eventUrl = `/web/events/${eventSlug}`
  const router = useRouter()

  const deleteTeamFunc = async () => {
    if (!isTeamLeader) return

    const result = await deleteTeam(eventSlug, team.id)

    if (result.message) {
      toast(result.message)
      router.push(eventUrl)
    } else if (result.error) {
      toast(result.error, 'error')
    }
  }

  const leaveTeamFunc = async () => {
    if (!isMember) return

    const result = await leaveTeam(eventSlug, team.id)

    if (result.message) {
      toast(result.message)
      router.push(eventUrl)
    } else if (result.error) {
      toast(result.error, 'error')
    }
  }

  if (!submissionStarted) {
    return (
      <CtaPanel
        header="Submissions have not begun - yet. Hang tight! 👀"
        subHeader="We can't wait to see your awesome creations. Until then, explore other hackathons or connect with fellow participants!"
        cta={{ text: 'Explore Hackathons', url: '/web/events' }}
      ></CtaPanel>
    )
  }

  if (!team) {
    const firstRoundEnded = isPast(rounds[0].submission_deadline)

    return (
      <>
        {firstRoundEnded ? (
          <CtaPanel
            header="Submission for this hackathon has ended."
            subHeader="We're sorry it did not work out this time. We appreciate your interest, and we encourage you to explore more opportunities for innovation with us in the future!"
            cta={{ text: 'Explore Hackathons', url: '/web/events' }}
          ></CtaPanel>
        ) : (
          <CtaPanel
            header="Invite teammates and start crafting your winning entry now! 🤩"
            subHeader="By starting the submission, you will be assigned the team leader role; only you will be able to add team members and submit on behalf of the team."
            cta={{
              text: 'Create Team',
              url: `/web/events/${eventSlug}/team/create`,
            }}
          ></CtaPanel>
        )}
      </>
    )
  }

  return (
    <>
      {/* {rounds?.map((round) => ( */}
      <MySubmissionSummary
        key={rounds[0]?.id}
        isTeamLeader={isTeamLeader}
        eventSlug={eventSlug}
        themes={themes}
        round={rounds[0]}
      />
      {/* ))} */}

      <TeamPanel
        eventSlug={eventSlug}
        isLeader={isTeamLeader}
        isMember={isMember}
        isfirstRoundEnded={isPast(rounds[0].submission_deadline)}
        team={team}
        currentUser={session && session.user}
      />
      <TeamAction
        isLeader={isTeamLeader}
        isMember={isMember}
        isfirstRoundEnded={isPast(rounds[0].submission_deadline)}
        submissionStatus={team.submission_status}
        deleteTeam={deleteTeamFunc}
        leaveTeam={leaveTeamFunc}
      />
    </>
  )
}
